import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../../helpers/api-client.helper';
import {
    APIResponse,
    BusinessOwner,
    ExistingLGA,
    MyTasksBusinessOwner,
    PaginationResponse,
    MyTasksRequestModel,
    BRMFVSStatusResponse,
    TasksBusinessOwner,
    FVSLocation,
    BulkTaskRejectionApiPayload,
    BulkTaskRejectionApiResponse,
    AssignToMeRequest,
    ProofOfAddressOption,
} from '../../models/fvs-address-verification.model';
import { AppConfig } from '../../../shared/config/app-config';

@Injectable({
    providedIn: 'root',
})
export class FVSDataService {
    readonly partnerManagementService = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/partner-management`;
    readonly verificationService = `${this.config.MONIEPOINT_FV_CORE}/api/v1/verifications`;

    constructor(private apiClientHelper: ApiClientHelper, public config: AppConfig) {}

    onboardBRMAddressOnFVS(stateCode: string, lgaCodes: string[]): Promise<APIResponse<{ message: string }>> {
        const url = `${this.partnerManagementService}/api/v1/fvs/lga/update`;

        return this.apiClientHelper.putForPromise(url, { stateCode, lgaCodes });
    }

    getFVSPendingVerificationCount() {
        const url = `${this.partnerManagementService}/api/v1/fvs/pending-verification-count`;
        return this.apiClientHelper.getForPromise(url);
    }

    getFVSLoginReference() {
        const url = `${this.config.MONIEPOINT_FV_CORE}/api/v1/token-reference`;

        return this.apiClientHelper.postForPromise(url, {});
    }

    getAllBOs(page: number, size: number, searchTerm?: string) {
        const url = `${this.partnerManagementService}/api/v1/priority-bos/all?page=${page}&size=${size}&businessName=${searchTerm}`;

        return this.apiClientHelper.getForPromise(url);
    }

    updatePriorityBOList(boCodesMarked: string[], boCodesUnmarked: string[]) {
        const url = `${this.partnerManagementService}/api/v1/priority-bos`;

        return this.apiClientHelper.postForPromise(url, {
            boCodesMarked,
            boCodesUnmarked,
        });
    }

    getPriorityBos(params: string): Promise<APIResponse<PaginationResponse<BusinessOwner>>> {
        const url = `${this.partnerManagementService}/api/v1/priority-bos/?${params}`;

        return this.apiClientHelper.getForPromise(url);
    }

    getMyTasksBos(params: MyTasksRequestModel): Promise<PaginationResponse<MyTasksBusinessOwner>> {
        const url = `${this.verificationService}/pending`;

        return this.apiClientHelper.postForPromise(url, params);
    }

    getUnAssignedTasksBos(urlString?: string): Promise<PaginationResponse<TasksBusinessOwner>> {
        const url = `${this.verificationService}/unassigned?${urlString}`;

        return this.apiClientHelper.getForPromise(url);
    }

    getExpiredTasksBos(urlString?: string): Promise<PaginationResponse<TasksBusinessOwner>> {
        const url = `${this.verificationService}/overdue?${urlString}`;

        return this.apiClientHelper.getForPromise(url);
    }

    getExistingLGAsOnFVS(): Promise<APIResponse<ExistingLGA>> {
        const url = `${this.partnerManagementService}/api/v1/fvs/lga`;

        return this.apiClientHelper.getForPromise(url);
    }

    getBRMStatusOnFVS(): Promise<APIResponse<BRMFVSStatusResponse>> {
        const url = `${this.partnerManagementService}/api/v1/fvs/brm-fvs-status`;

        return this.apiClientHelper.getForPromise(url);
    }

    getLGAsByStateCodeFromFVS(code: string): Promise<FVSLocation[]> {
        const url = `${this.config.MONIEPOINT_FV_CORE}/api/v1/config/states/${code}/lgas`;

        return this.apiClientHelper.getForPromise(url);
    }

    getStatesFromFVS(): Promise<FVSLocation[]> {
        const url = `${this.config.MONIEPOINT_FV_CORE}/api/v1/config/states`;

        return this.apiClientHelper.getForPromise(url);
    }

    rejectTasks(payload: BulkTaskRejectionApiPayload): Promise<BulkTaskRejectionApiResponse> {
        const url = `${this.verificationService}/bulk-reject`;

        return this.apiClientHelper.postForPromise(url, payload);
    }

    assignToMe(payload: AssignToMeRequest): Promise<APIResponse> {
        const url = `${this.verificationService}/${payload.clientRequestReference}/assign-to-self`;
        return this.apiClientHelper.patchForPromise(url, payload);
    }

    uploadFile(file) {
		const formData = new FormData();
		formData.append('file', file);
		const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v2/business-kycs/registered-business/document/upload`;
		return this.apiClientHelper.putForPromiseMultipart(url, formData);
	}

    fetchProofOfAddressOptions(): Promise<ProofOfAddressOption[]> {
        const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v2/business-kycs/proof-of-address-types`;
        return this.apiClientHelper.getForPromise(url);
    }
}
